import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import { useStyles } from "Components/Shared/Table/Table.styles";
import * as React from "react";
import { TableCollectionPagination } from "./TableCollectionPagination";

export interface HeaderGroup {
    id: string;
    label: React.ReactNode;
    colSpan?: number;
    rowSpan?: number;
    align?: 'left' | 'center' | 'right';
}

export interface GroupedColumn<T> {
    key: string;
    width?: number;
    align?: 'left' | 'center' | 'right';
    renderHead: () => React.ReactNode;
    renderSkeleton: () => React.ReactNode;
    renderCell: (props: { row: T }) => React.ReactNode;
}

interface Props<T> {
    loading: boolean;
    headerGroups: HeaderGroup[];
    columns: GroupedColumn<T>[];
    rows: T[];
    getRowId: (row: T) => string;
    totalCount: number;
    page: number;
    rowsPerPage: number;
    onChangePage: (page: number) => void;
    onChangeRowsPerPage: (rowsPerPage: number) => void;
}

function TableGroupedHeader<T>({
    loading,
    headerGroups,
    columns,
    rows,
    getRowId,
    totalCount,
    page,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage
}: Readonly<Props<T>>) {
    const classes = useStyles();

    // Determine which column indices are covered by a headerGroup with rowSpan >= 2
    // so they don't render a sub-header cell in the second row
    const hiddenSubHeaderIndices = new Set<number>();
    let colIndex = 0;
    for (const group of headerGroups) {
        const span = group.colSpan ?? 1;
        if ((group.rowSpan ?? 1) >= 2) {
            for (let i = 0; i < span; i++) {
                hiddenSubHeaderIndices.add(colIndex + i);
            }
        }
        colIndex += span;
    }

    return (
        <>
            <TableContainer>
                <Table className={classes.tableRoot}>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            {headerGroups.map((group) => (
                                <TableCell
                                    key={group.id}
                                    colSpan={group.colSpan ?? 1}
                                    rowSpan={group.rowSpan ?? 1}
                                    align={group.align}
                                    style={
                                        (group.rowSpan ?? 1) >= 2
                                            ? { verticalAlign: 'center' }
                                            : undefined
                                    }
                                >
                                    {group.label}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {columns.map((column, index) => {
                                if (hiddenSubHeaderIndices.has(index)) return null;
                                return (
                                    <TableCell
                                        key={column.key}
                                        align={column.align}
                                        style={{
                                            width: column.width == null
                                                ? 'auto'
                                                : `${column.width * 100}%`
                                        }}
                                    >
                                        {column.renderHead()}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading
                            ? Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
                                <TableRow key={`skeleton-row-${page}-${rowIndex}`}>
                                    {columns.map((column) => (
                                        <TableCell key={column.key} align={column.align}>
                                            {column.renderSkeleton()}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                            : rows.map((row) => (
                                <TableRow key={getRowId(row)}>
                                    {columns.map((column) => (
                                        <TableCell key={column.key} align={column.align}>
                                            {column.renderCell({ row })}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TableCollectionPagination
                totalCount={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onChangePage={onChangePage}
                onChangeRowsPerPage={onChangeRowsPerPage}
            />
        </>
    );
}

export default TableGroupedHeader;
